// The own hand's arrangement (design 8 §2, P3): sorted while auto-sort is on; otherwise the player's own
// order, kept across draws, calls and discards, with each new tile placed at the right. The slots a discard
// reports come from this order, so other seats see what the player's hand really showed.

import { create } from "@bufbuild/protobuf";
import { HandSlotsSchema } from "../pb/mj/record/v1/record_pb";
import type { HandSlots } from "../pb/mj/record/v1/record_pb";
import { NO_TILE, sortTiles } from "./tiles";

/** The hand in `prev`'s order: the tiles still held where they were, the new ones at the right. */
export function arrange(prev: readonly number[], concealed: readonly number[]): number[] {
  const held = new Set(concealed);
  const kept = prev.filter((t) => held.has(t));
  const known = new Set(kept);
  return [...kept, ...concealed.filter((t) => !known.has(t))];
}

/** `order` with `tile` moved to slot `to` (counted in the order without it). */
export function move(order: readonly number[], tile: number, to: number): number[] {
  const rest = order.filter((t) => t !== tile);
  if (rest.length === order.length) return [...order];
  rest.splice(Math.max(0, Math.min(to, rest.length)), 0, tile);
  return rest;
}

/** The slots a call's or kan's tiles from the hand leave, ascending, in the hand shown as `order` with
 *  `drawn` apart at the right (design 10 T11). */
export function callSlotsOf(order: readonly number[], drawn: number, tiles: readonly number[]): number[] {
  const shown = drawn !== NO_TILE ? [...order, drawn] : order;
  return tiles.map((t) => shown.indexOf(t)).filter((i) => i >= 0).sort((a, b) => a - b);
}

/** The slots of discarding `tile` from the hand shown as `order` with `drawn` apart. */
export function slotsOf(order: readonly number[], drawn: number, tile: number, autoSort: boolean): HandSlots | undefined {
  const shown = drawn !== NO_TILE ? [...order, drawn] : order;
  const from = shown.indexOf(tile);
  if (from < 0) return undefined;
  const keptDraw = drawn !== NO_TILE && tile !== drawn;
  let to = 0;
  if (keptDraw) {
    const rest = order.filter((t) => t !== tile);
    to = (autoSort ? sortTiles([...rest, drawn]) : [...rest, drawn]).indexOf(drawn);
  }
  return create(HandSlotsSchema, { from, keptDraw, to });
}

